import { useEffect, useMemo, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Tabs } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import {
  StayVisibleModal,
  StayVisibleRepairModal,
} from '@/components/stay-visible-modal';
import { useStayVisibleStore } from '@/state/stay-visible-store';
import { createCueSocket } from '@/api/cue-socket';
import { createEventsSocket } from '@/api/events-socket';
import { socketRecovery } from '@/api/socket-recovery';
import { createResyncGate } from '@/features/realtime/resync-gate';
import { onForeground } from '@/lib/foreground';
import { createCoalescedRefresh } from '@/state/inbox-sync';
import { USE_MOCK } from '@/api/config';
import { ApiError } from '@/api/client';
import {
  presentLocalNotification,
  registerForPushNotifications,
} from '@/lib/notifications';
import { NotificationNudge } from '@/components/notification-nudge';
import { syncReflectionReminder } from '@/lib/reflection-reminder';
import { syncDeviceTimezone } from '@/lib/timezone-sync';
import { useAuthStore } from '@/state/auth-store';
import { useSessionStore } from '@/state/session-store';
import {
  RECONNECT_JITTER_MS,
  RECONNECT_PHASE_GAP_MS,
} from "@/state/refresh-schedule";
import { useAppTheme } from '@/theme/app-theme';
import { socialStrings as strings } from '@/i18n/features/social.i18n';

const TAB_BAR_BACKGROUND = '#19283B';
const TAB_BAR_ACTIVE = '#78AEFF';
const TAB_BAR_INACTIVE =
  'rgba(255, 255, 255, 0.68)';
const TAB_BAR_SHADOW = '#102D51';
const NEW_PEOPLE_NOTIFICATION_WINDOW_MS = 2500;

// Realtime sockets are kept on a module-global so there is only ever ONE of
// each, even across dev Fast Refresh (which re-runs effects without always
// disposing the previous module instance). Duplicate sockets would deliver every
// event — new messages, cues, notifications — multiple times.
type SocketHandle = { disconnect: () => void };
type PendingPeopleJoinedNotification = {
  displayName?: string;
  userId: string;
};
const realtimeSockets = globalThis as unknown as {
  __opncueCueSocket?: SocketHandle | null;
  __opncueEventsSocket?: SocketHandle | null;
};

/**
 * Kick off a refresh from a socket handler without waiting for it.
 *
 * These run in response to a server event rather than anything the user did,
 * and there is no screen here to report to — each store records its own failure
 * state and the panels render it. A bare `void` would leave a rejected promise
 * that nobody owns, which surfaces as an uncaught error on top of whatever the
 * user happens to be looking at.
 */
function refreshQuietly(run: () => Promise<unknown>): void {
  void run().catch(() => {});
}

// Location is required to be in here at all, but the asking happens on the
// onboarding permissions screen — the navigation guard sends anyone whose
// location has stopped working back to it.
/**
 * The floor between event-driven nearby reads.
 *
 * Matches `createCoalescedRefresh`'s own default window rather than inventing a
 * second number: the burst window and the stream rate answer the same question
 * — how stale may this list be — and one number is easier to reason about than
 * two that drift. Chosen for staleness, not for load: a nearby row is a claim
 * that somebody is here NOW, and fifteen seconds is inside what that claim can
 * survive. The load relief is a consequence, and the real fix for load is
 * server-side batching (`docs/architecture/arrival-fan-out.md`, Stage 2).
 */
const NEARBY_EVENT_MIN_INTERVAL_MS = 15_000;

export default function TabsLayout() {
  const { t } = useTranslation();
  const { colors } = useAppTheme();
  const insets = useSafeAreaInsets();
  const peopleJoinedBufferRef = useRef<PendingPeopleJoinedNotification[]>([]);
  const peopleJoinedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Since the app is edge-to-edge, the Android gesture/navigation bar reports a
  // bottom inset. Grow the tab bar into it (background fills the area) and pad
  // the labels up, so there's no navy gap below the tab bar. iOS keeps its
  // existing height (already accounts for the home indicator) and web uses no
  // extra inset — otherwise the browser's safe-area inset leaves a weird empty
  // strip at the bottom of the page.
  const tabBarBottomInset = Platform.OS === 'android' ? insets.bottom : 0;

  const pendingCueCount = useSessionStore(
    (state) => state.inbox.length,
  );

  const unreadMessageCount = useSessionStore((state) => {
    const chatCount = Object.values(state.unreadByChat).filter(
      (count) => count > 0,
    ).length;
    const groupCount = state.groupInbox.filter(
      // Ended groups keep their history but must not keep a red badge: nothing
      // new can ever arrive in one, so "unread" there is not a call to action.
      (group) =>
        group.unreadCount > 0 && (group.state ?? 'active') === 'active',
    ).length;
    return chatCount + groupCount;
  });

  const accessToken = useAuthStore(
    (state) => state.session?.accessToken,
  );
  const currentUserId = useAuthStore(
    (state) => state.user?.id ?? null,
  );

  const notificationPreferences = useAuthStore(
    (state) => state.user?.notificationPreferences,
  );
  // Held in a ref so the realtime socket handlers always read the latest prefs
  // without the socket effects depending on (and re-connecting for) every prefs
  // change — reconnect churn was a source of duplicate connections.
  const notificationPreferencesRef = useRef(notificationPreferences);
  useEffect(() => {
    notificationPreferencesRef.current = notificationPreferences;
  }, [notificationPreferences]);

  const reflectionFrequency = useAuthStore(
    (state) => state.user?.reflectionFrequency,
  );
  // Custom-cadence details also drive the schedule, so re-arm when they change.
  const reflectionReminderTime = useAuthStore(
    (state) => state.user?.reflectionReminderTime,
  );
  const reflectionReminderDaysKey = useAuthStore((state) =>
    (state.user?.reflectionReminderDays ?? []).join(","),
  );

  const clearLocalSession = useAuthStore(
    (state) => state.clearLocalSession,
  );

  const receiveCue = useSessionStore(
    (state) => state.receiveCue,
  );

  const receiveChatMessage = useSessionStore(
    (state) => state.receiveChatMessage,
  );

  const receiveChatReveal = useSessionStore(
    (state) => state.receiveChatReveal,
  );

  const receiveGroupMessage = useSessionStore(
    (state) => state.receiveGroupMessage,
  );

  const removeNearbyUser = useSessionStore(
    (state) => state.removeNearbyUser,
  );

  const markNearbyDirty = useSessionStore(
    (state) => state.markNearbyDirty,
  );

  const refreshInbox = useSessionStore(
    (state) => state.refreshInbox,
  );

  const refreshGroupInbox = useSessionStore(
    (state) => state.refreshGroupInbox,
  );

  const refreshChats = useSessionStore(
    (state) => state.refreshChats,
  );

  const refreshNearby = useSessionStore(
    (state) => state.refreshNearby,
  );

  const refreshAll = useSessionStore(
    (state) => state.refreshAll,
  );

  /**
   * Re-read everything the realtime channel maintains.
   *
   * Reached whenever we may have stopped listening without noticing: a socket
   * reconnect, or the app coming back from the background. Nothing on `/events`
   * or `/cue` is replayable, so a gap can only be closed by asking again.
   *
   * State only — no notifications are raised for what was missed. Waking a phone
   * that slept overnight should not set off a burst of buzzes for things the
   * user cannot act on any more; that is what real push is for.
   */
  const resync = useMemo(
    // RECONNECT opts into the scheduler; the launch path below does not.
    //
    // A dropped task disconnects every client at once and they all come back
    // together, so this is the synchronised case — and it is the one where a
    // delay is safe, because the screens already hold data from before the
    // drop. Nearby lagging a few seconds here shows STALE people, not an empty
    // list; on a cold open it would show empty, which is why launch is left
    // alone until there is a loading state for it.
    () =>
      createResyncGate(() =>
        refreshAll({
          jitterMs: RECONNECT_JITTER_MS,
          gapMs: RECONNECT_PHASE_GAP_MS,
        }),
      ),
    [refreshAll],
  );

  /**
   * The forced nearby re-read behind `people:joined` / `people:hidden`.
   *
   * Forced, because a socket event is precisely "a state change the gate
   * cannot see" (`launch-refresh.ts`): unforced, an event landing within the
   * gate's freshness window was silently skipped, and the list stayed wrong
   * until the next tab switch. Coalesced, because arrivals come in bursts —
   * the in-flight guard collapses a burst into one read plus at most one
   * follow-up for whatever the first read started too early to include.
   */
  const nearbyEventRefresh = useMemo(
    () =>
      createCoalescedRefresh(
        () => refreshNearby({ force: true, background: true }),
        // The floor a busy venue needs. `intervalMs` alone bounds a burst and
        // arrivals in a crowd are not a burst, they are a stream — see the
        // option's own comment and `docs/architecture/arrival-fan-out.md`.
        // Same number as the window, so a stream costs one read per window
        // whichever call site asked for it.
        { minIntervalMs: NEARBY_EVENT_MIN_INTERVAL_MS },
      ),
    [refreshNearby],
  );

  // Coming back from the background is the common way to miss things: both
  // sockets are torn down on a sleeping phone and reconnect a moment after the
  // app wakes. Asking here as well as on reconnect means the state is already
  // being re-read by the time the first tab renders — and the gate makes the
  // overlap free. Deliberately not gated on notification preferences: someone
  // who muted their buzzes still needs a current inbox.
  useEffect(() => {
    if (!accessToken) return undefined;
    //
    // Rate limited on top of the gate's own window, because those are two
    // different jobs. The gate collapses the BURST around one resume (this
    // listener plus two socket reconnects, ~3s apart). This caps how often a
    // resume may happen at all, which on web is how often the user changes
    // tab. A short flip missed nothing — the WebSocket stays open in a hidden
    // tab — and a flip long enough to lose the socket comes back through
    // `onConnect` below, which is not rate limited and still catches up.
    return onForeground(
      'session.resync',
      () => resync.request(),
      // Primed, and the work is real even though it is not on this line: the
      // sibling effect below runs `refreshAll()` on the same `accessToken`, in
      // the same component, at mount. Without this a tab flip within the first
      // minute of launch would re-read all six — and the resync gate cannot
      // stop it, because `loadSession` does not go through the gate.
      { justRan: true },
    );
  }, [accessToken, resync]);

  // A server REFUSAL leaves the sockets in a disconnect socket.io never
  // retries; the shared coordinator's timers do that. They pause in the
  // background — a suspended app cannot usefully retry — and MUST resume on
  // foreground, because the HTTP resync above never recreates the sockets
  // (docs/architecture/realtime-refusal-recovery.md).
  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') socketRecovery.resume();
      else socketRecovery.pause();
    });
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }

    let active = true;

    async function loadSession() {
      try {
        await refreshAll();
      } catch (error) {
        if (!active) {
          return;
        }

        if (error instanceof ApiError && error.status === 401) {
          await clearLocalSession();
          return;
        }

        console.error(
          'Unable to refresh app data:',
          error,
        );
      }
    }

    void loadSession();

    return () => {
      active = false;
    };
  }, [accessToken, clearLocalSession, refreshAll]);

  useEffect(() => {
    if (!accessToken || USE_MOCK) return undefined;

    const notificationsEnabled =
      !notificationPreferences?.muteAll &&
      ((notificationPreferences?.newPeople ?? true) ||
        (notificationPreferences?.cues ?? true) ||
        (notificationPreferences?.messages ?? true) ||
        (notificationPreferences?.goalReminders ?? true));

    if (!notificationsEnabled) return undefined;

    if (Platform.OS !== 'web') {
      void registerForPushNotifications({ requestPermission: false });
      return undefined;
    }

    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      globalThis.Notification.permission === 'granted'
    ) {
      void registerForPushNotifications({ requestPermission: false });
    }

    return undefined;
  }, [accessToken, notificationPreferences]);

  useEffect(() => {
    if (!accessToken || USE_MOCK || Platform.OS === 'web') return undefined;

    const notificationsEnabled =
      !notificationPreferences?.muteAll &&
      ((notificationPreferences?.newPeople ?? true) ||
        (notificationPreferences?.cues ?? true) ||
        (notificationPreferences?.messages ?? true) ||
        (notificationPreferences?.goalReminders ?? true));

    if (!notificationsEnabled) return undefined;

    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        void registerForPushNotifications({ requestPermission: false });
      }
    });

    return () => subscription.remove();
  }, [accessToken, notificationPreferences]);

  useEffect(() => {
    if (!accessToken || USE_MOCK) return undefined;

    // Dispose any prior connection (incl. a leaked dev hot-reload one) first, so
    // there is exactly one cue socket delivering each event once.
    realtimeSockets.__opncueCueSocket?.disconnect();
    const socket = createCueSocket(accessToken, {
      onConnect: () => resync.request(),
      onCueReceived: (cue) => {
        receiveCue(cue);
        refreshQuietly(refreshInbox);
        refreshQuietly(refreshNearby);

        void presentLocalNotification(
          t(strings.cueReceivedTitle, {
            name: cue.fromName || t(strings.someoneNearby),
          }),
          {
            body: t(strings.cueReceivedBody),
            data: { type: 'cue', cueId: cue.id },
            category: 'cues',
          },
          notificationPreferencesRef.current,
        );
      },
    });
    realtimeSockets.__opncueCueSocket = socket;

    return () => {
      socket.disconnect();
      if (realtimeSockets.__opncueCueSocket === socket) {
        realtimeSockets.__opncueCueSocket = null;
      }
    };
  }, [accessToken, receiveCue, refreshInbox, refreshNearby, resync, t]);

  useEffect(() => {
    if (!accessToken || USE_MOCK) return undefined;

    // Dispose any prior connection (incl. a leaked dev hot-reload one) first, so
    // each event is delivered exactly once — otherwise unread counts and
    // notifications multiply.
    realtimeSockets.__opncueEventsSocket?.disconnect();
    const socket = createEventsSocket(accessToken, {
      onConnect: () => resync.request(),
      onCueAccepted: (event) => {
        receiveChatMessage(event.chatId, event.cueId);
        void presentLocalNotification(
          t(strings.cueAcceptedTitle, {
            name: event.acceptedByName || t(strings.someone),
          }),
          {
            body: t(strings.cueAcceptedBody),
            data: { type: 'cue_accepted', chatId: event.chatId },
            category: 'messages',
          },
          notificationPreferencesRef.current,
        );
      },
      onChatMessage: (event) => {
        // The payload is what the inbox row now holds, so pass it through and
        // let the store patch instead of re-reading the inbox per message.
        receiveChatMessage(event.chatId, event.messageId, {
          preview: event.preview,
          sentAt: event.sentAt,
        });
        // Don't buzz for a conversation the user is actively reading.
        if (useSessionStore.getState().activeChatId === event.chatId) {
          return;
        }
        void presentLocalNotification(
          t(strings.messageTitle, {
            name: event.senderName || t(strings.someone),
          }),
          {
            body: event.preview || t(strings.messageBody),
            data: { type: 'message', chatId: event.chatId },
            category: 'messages',
          },
          notificationPreferencesRef.current,
        );
      },
      onGroupMessage: (event) => {
        receiveGroupMessage(event.groupId, {
          preview: event.preview,
          sentAt: event.sentAt,
        });
        // Don't buzz for a group the user is actively reading.
        if (useSessionStore.getState().activeGroupId === event.groupId) {
          return;
        }
        void presentLocalNotification(
          t(strings.groupMessageTitle, {
            name: event.senderName || t(strings.someone),
            groupName: event.groupName || t(strings.smallGroup),
          }),
          {
            body: event.preview || t(strings.groupMessageBody),
            data: { type: 'group_message', groupId: event.groupId },
            category: 'messages',
          },
          notificationPreferencesRef.current,
        );
      },
      onGroupMembers: () => {
        refreshQuietly(refreshGroupInbox);
      },
      onGroupRemoved: () => {
        refreshQuietly(refreshGroupInbox);
      },
      onPeopleJoined: (event) => {
        // Still `requestNow`, and still immediate in a quiet space — the floor
        // inside the coalescer is what degrades the second and later arrivals
        // within the window to a scheduled read. An arrival is not urgent
        // enough to be worth a read each; somebody who appeared ten seconds
        // ago is still there.
        nearbyEventRefresh.requestNow();
        peopleJoinedBufferRef.current = [
          ...peopleJoinedBufferRef.current,
          {
            displayName: event.displayName,
            userId: event.userId,
          },
        ];

        if (peopleJoinedTimerRef.current) {
          return;
        }

        peopleJoinedTimerRef.current = setTimeout(() => {
          const people = peopleJoinedBufferRef.current;
          peopleJoinedBufferRef.current = [];
          peopleJoinedTimerRef.current = null;

          const first = people[0];
          const hasMultiplePeople = people.length > 1;
          void presentLocalNotification(
            hasMultiplePeople
              ? t(strings.morePeopleTitle)
              : t(strings.onePersonTitle, {
                  name: first?.displayName || t(strings.someone),
                }),
            {
              body: hasMultiplePeople
                ? t(strings.morePeopleBody)
                : t(strings.onePersonBody),
              data: hasMultiplePeople
                ? { type: 'new_person', count: people.length }
                : { type: 'new_person', userId: first?.userId },
              category: 'newPeople',
            },
            notificationPreferencesRef.current,
          );
        }, NEW_PEOPLE_NOTIFICATION_WINDOW_MS);
      },
      onNearbyChanged: () => {
        // Stage 4. A returning person no longer arrives as an identity event,
        // because naming them cost an audience selection, a spatial query,
        // five eligibility queries and up to 200 emits — per arrival — and
        // that is what took a stg task down
        // (docs/architecture/arrival-fan-out.md). Selection rather than fetch:
        // the server's 3 s audience cache shares one adapter read across a
        // burst, so the unconditional per-arrival cost is everything after
        // the audience.
        //
        // So this is the whole handler, and the two things it does NOT do are
        // the point. It does not patch state, because the event names nobody
        // and there is nothing to patch. And it does not notify: the server
        // sends this exactly for the arrivals it decided should buzz no one,
        // and a local buzz here would reintroduce, on the client, the thing
        // the server stopped doing.
        //
        // GATED ON THE LIST BEING ON SCREEN, which is the difference between
        // moving the cost and removing it. The identity fan-out this replaced
        // was filtered server-side to at most 200 eligible recipients; a room
        // broadcast is filtered by nothing and reaches every socket in the
        // covering cells. Reading unconditionally there means a phone showing
        // Chat re-reads `/presence/available` every 15 s in a busy cell — the
        // one query with a measured density cliff (1161 ms at dense@10k,
        // BASELINE-SQL.md). Nobody is looking at that list, so nobody is
        // served by the read.
        //
        // Invisible, the nudge costs one bit. Home drains it on its next
        // reconcile, which is already running on a clock whenever the list is
        // up — so the list is never knowingly stale in front of somebody, it
        // just stops being re-read in front of nobody.
        //
        // `AppState` is checked alongside the flag rather than folded into it
        // because the two go out of step on web: a sleeping phone tears the
        // socket down and no nudge arrives at all, but a HIDDEN BROWSER TAB
        // keeps its socket and keeps Home focused, so the flag alone would
        // read on every nudge behind a tab nobody has looked at in an hour.
        if (
          AppState.currentState !== 'active' ||
          !useSessionStore.getState().nearbyListVisible
        ) {
          markNearbyDirty();
          return;
        }
        // `requestNow` is already floored to one read per window, so a whole
        // cell nudged at once still costs this client one refresh.
        nearbyEventRefresh.requestNow();
      },
      onPeopleHidden: (event) => {
        // The removal is applied directly — the event names the row — and the
        // read behind it converges anything else that changed with it.
        //
        // ORDER IS LOAD BEARING. `removeNearbyUser` is the entire privacy
        // action and runs first, unthrottled, unconditionally: it is the only
        // thing that takes a hidden person off this screen promptly. The read
        // behind it is speculative backfill — a page of 75 minus one leaves a
        // gap the next page should fill — and backfill is what the floor is
        // allowed to delay. Nothing may ever be placed in front of the
        // removal, however cheap it looks.
        removeNearbyUser(event.userId);
        nearbyEventRefresh.requestNow();
      },
      onRevealRequested: (event) => {
        const patched = receiveChatReveal(event.chatId, {
          status: 'pending_peer',
          actorId: event.requesterId,
          currentUserId,
        });
        if (!patched) refreshQuietly(refreshChats);
        if (useSessionStore.getState().activeChatId === event.chatId) {
          return;
        }
        void presentLocalNotification(
          t(strings.revealRequestedTitle, {
            name: event.requesterName || t(strings.someone),
          }),
          {
            body: t(strings.revealRequestedBody),
            data: { type: 'reveal_request', chatId: event.chatId },
            category: 'messages',
          },
          notificationPreferencesRef.current,
        );
      },
      onRevealCompleted: (event) => {
        const patched = receiveChatReveal(event.chatId, {
          status: 'revealed',
          actorId: event.acceptedByUserId,
          currentUserId,
        });
        if (!patched) refreshQuietly(refreshChats);
        if (useSessionStore.getState().activeChatId === event.chatId) {
          return;
        }
        void presentLocalNotification(
          t(strings.revealCompletedTitle, {
            name: event.acceptedByName || t(strings.someone),
          }),
          {
            body: t(strings.revealCompletedBody),
            data: { type: 'reveal_completed', chatId: event.chatId },
            category: 'messages',
          },
          notificationPreferencesRef.current,
        );
      },
    });
    realtimeSockets.__opncueEventsSocket = socket;

    return () => {
      if (peopleJoinedTimerRef.current) {
        clearTimeout(peopleJoinedTimerRef.current);
        peopleJoinedTimerRef.current = null;
      }
      peopleJoinedBufferRef.current = [];
      nearbyEventRefresh.cancel();
      socket.disconnect();
      if (realtimeSockets.__opncueEventsSocket === socket) {
        realtimeSockets.__opncueEventsSocket = null;
      }
    };
  }, [
    accessToken,
    currentUserId,
    markNearbyDirty,
    nearbyEventRefresh,
    receiveChatMessage,
    receiveChatReveal,
    receiveGroupMessage,
    refreshChats,
    removeNearbyUser,
    refreshGroupInbox,
    refreshInbox,
    refreshNearby,
    resync,
    t,
  ]);

  // Reflection reminder: reconcile the scheduled local notification with the
  // user's cadence (evenings one-shot / weekly / custom / off). Re-runs when the
  // session, cadence, or custom day/time changes.
  useEffect(() => {
    if (!accessToken) return;
    // Keep the server's copy of the device time zone current so the
    // reminder-email cron fires at the user's real local time.
    void syncDeviceTimezone();
    void syncReflectionReminder();
  }, [
    accessToken,
    reflectionFrequency,
    reflectionReminderTime,
    reflectionReminderDaysKey,
  ]);

  // Load the stay-visible prefs and count each app open (drives the
  // exponential-backoff reminder for "app open only" users).
  useEffect(() => {
    let active = true;
    void (async () => {
      await useStayVisibleStore.getState().hydrate();
      if (!active) return;
      useStayVisibleStore.getState().recordAppOpen();
      // Detect broken location/permission state and repair on launch.
      void useStayVisibleStore.getState().verify();
      // Re-arm the evening reflection reminder for today's activity.
      void syncReflectionReminder();
    })();
    const sub = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;
      useStayVisibleStore.getState().recordAppOpen();
      void useStayVisibleStore.getState().verify();
      void syncReflectionReminder();
    });
    return () => {
      active = false;
      sub.remove();
    };
  }, []);

  return (
    <>
    <Tabs
      screenOptions={{
        headerShown: false,

        sceneStyle: {
          backgroundColor:
            colors.background,
        },

        tabBarActiveTintColor:
          TAB_BAR_ACTIVE,

        tabBarInactiveTintColor:
          TAB_BAR_INACTIVE,

        tabBarHideOnKeyboard: true,

        tabBarStyle: {
          height:
            (Platform.OS === 'ios'
              ? 88
              : 64) + tabBarBottomInset,

          paddingBottom: tabBarBottomInset,
          paddingTop: 6,
          borderTopWidth: 0,

          backgroundColor:
            TAB_BAR_BACKGROUND,

          shadowColor:
            TAB_BAR_SHADOW,
          shadowOpacity: 0.22,
          shadowRadius: 12,
          shadowOffset: {
            width: 0,
            height: -4,
          },

          elevation: 10,
        },

        tabBarLabelStyle: {
          fontSize: 11,
          lineHeight: 15,
          fontWeight: '600',
        },

        tabBarIconStyle: {
          marginTop: 1,
        },
      }}>
      <Tabs.Screen
        name="home"
        options={{
          title: t(strings.navHome),

          tabBarIcon: ({
            color,
            size,
            focused,
          }) => (
            <Ionicons
              name={
                focused
                  ? 'location'
                  : 'location-outline'
              }
              size={size}
              color={color}
            />
          ),
        }}
      />

      <Tabs.Screen
        name="cues"
        options={{
          title: t(strings.navCues),

          tabBarBadge:
            pendingCueCount > 0
              ? pendingCueCount
              : undefined,

          tabBarBadgeStyle: {
            minWidth: 18,
            height: 18,
            paddingHorizontal: 4,
            borderRadius: 9,
            borderWidth: 1,
            borderColor:
              TAB_BAR_BACKGROUND,
            backgroundColor:
              '#FFFFFF',
            color:
              TAB_BAR_BACKGROUND,
            fontSize: 10,
            lineHeight: 18,
            fontWeight: '700',
            textAlign: 'center',
            textAlignVertical: 'center',
          },

          tabBarIcon: ({
            color,
            size,
            focused,
          }) => (
            <Ionicons
              name={
                focused
                  ? 'chatbubbles'
                  : 'chatbubbles-outline'
              }
              size={size}
              color={color}
            />
          ),
        }}
      />

      <Tabs.Screen
        name="inbox"
        options={{
          title: t(strings.navInbox),

          tabBarBadge:
            unreadMessageCount > 0
              ? unreadMessageCount
              : undefined,

          tabBarBadgeStyle: {
            minWidth: 18,
            height: 18,
            paddingHorizontal: 4,
            borderRadius: 9,
            borderWidth: 1,
            borderColor:
              TAB_BAR_BACKGROUND,
            backgroundColor:
              '#FFFFFF',
            color:
              TAB_BAR_BACKGROUND,
            fontSize: 10,
            lineHeight: 18,
            fontWeight: '700',
            textAlign: 'center',
            textAlignVertical: 'center',
          },

          tabBarIcon: ({
            color,
            size,
            focused,
          }) => (
            <Ionicons
              name={
                focused
                  ? 'mail'
                  : 'mail-outline'
              }
              size={size}
              color={color}
            />
          ),
        }}
      />

      <Tabs.Screen
        name="profile"
        options={{
          title: t(strings.navProfile),

          tabBarIcon: ({
            color,
            size,
            focused,
          }) => (
            <Ionicons
              name={
                focused
                  ? 'person-circle'
                  : 'person-circle-outline'
              }
              size={size}
              color={color}
            />
          ),
        }}
      />

      <Tabs.Screen
        name="edit-profile"
        options={{
          href: null,
          headerShown: false,
          tabBarStyle: { display: 'none' },
        }}
      />

      <Tabs.Screen
        name="edit-avatar"
        options={{
          href: null,
          headerShown: false,
        }}
      />

      <Tabs.Screen
        name="vibe-card"
        options={{
          href: null,
          headerShown: false,
        }}
      />

      <Tabs.Screen
        name="mood-jar"
        options={{
          href: null,
          headerShown: false,
        }}
      />

      <Tabs.Screen
        name="guest-profile"
        options={{
          href: null,
          headerShown: false,
        }}
      />
    </Tabs>

    <StayVisibleModal />

    <StayVisibleRepairModal />

    {/* Above the tab bar, below the modals: the "notifications are off"
        reminder. Mounted here rather than per screen so it survives tab
        switches, and inside the signed-in layout so it can read the store's
        activity counters. */}
    <NotificationNudge />
    </>
  );
}
